import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../components/shared/Spinner";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import {
  mdShipmentHeaders,
  mdNestedShipmentHeaders,
  shipmentHeaders,
  smShipmentHeaders,
} from "./utils/headers";
import {
  searchShipment,
  updatePage,
  updateSort,
} from "../../redux/reducers/chinaTrackerReducer";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getChinaShipments } from "../../redux/dispatcher/chinaTrackerDispatcher";
import { translateHeaders, useTranslation } from "../../utils/useTranslation";
import { chinaShipmentTranslationMap } from "../../utils/translations";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import ChinaShipmentContainerSearchModal from "./partials/ChinaShipmentContainerSearchModal";
import ChinaShipmentGenerateFormModal from "./partials/ChinaShipmentGenerateFormModal";
import ChinaShipmentIncomingOrders from "./partials/ChinaShipmentIncomingOrders";
import { ChinaTrackerDataTableWithPagination3 } from "../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination3";
import ChinaShipmentEditFormModal from "./partials/ChinaShipmentEditFormModal";
import ChinaRecentShipmentsModal from "./partials/ChinaRecentShipmentsModal";
import * as DocumentPicker from "expo-document-picker";
import { pushFileToS3 } from "../../services/s3BucketService";
import { useToast } from "react-native-toast-notifications";
import { useAppTheme } from "../../hooks/useAppTheme";

export default function ChinaShipmentScreen({ navigation }: any) {
  const { colors: themeColors } = useAppTheme();
  const styles = createStyles(themeColors);

  const [modalOpen, setModalOpen] = useState<boolean>();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);
  const isFocused = useIsFocused();
  const [isRefresh, setRefresh] = useState(false);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      shipmentHeaders,
    );
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const screenWidth = Dimensions.get("window").width;
  const shouldStackUtilityCards =
    isSmallDevice || isMobileDevice || screenWidth <= 1400;
  const shouldUseCompactControls =
    isSmallDevice || isMobileDevice || screenWidth <= 1366;

  const { t } = useTranslation();
  const columnsToNotHaveThousandsSeparator: Array<string> = [
    "Packing #",
    "Order No",
    "China Item Code",
  ];

  const { language } = useSelector((state: any) => state.configurations);

  const dispatch = useDispatch<any>();

  const [incomingBellStatus, setIncomingBellStatus] = useState<boolean>(true);

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };

  const [nestedTableHeaders, setNestedTableHeaders] = useState<
    Record<string, Record<string, string | number | boolean>>
  >(translateHeaders(mdNestedShipmentHeaders, t, chinaShipmentTranslationMap));

  const dateFilterList = [
    { key: "Last 6 Months", label: t("last6Months") },
    { key: "Last Month", label: t("lastMonth") },
    { key: "Last Week", label: t("lastWeek") },
    { key: "Today", label: t("today") },
  ];
  const [selectedDateFilter, setSelectedDateFilter] = useState<string | null>(
    "Last 6 Months",
  );

  // All pagination/sort/status state comes from Redux — no local page state
  const {
    loading,
    shipments,
    totalCount,
    totalPages,
    pageSize,
    page,
    sort,
    status,
    search,
  } = useSelector((state: any) => state.chinaTracker.shipments);

  const fetchShipments = async (dateFilter?: string) => {
    let dateFrom, dateTo;

    if (dateFilter) {
      const range = getDateRange(dateFilter);
      dateFrom = range.dateFrom;
      dateTo = range.dateTo;
    }

    dispatch(
      getChinaShipments({
        page: page,
        pageSize: pageSize,
        sort: sort.direction === "asc" ? sort.key : "-" + sort.key,
        status: status,
        search: search,
        dateFrom,
        dateTo,
      }),
    );
  };
  const onClose = (shouldRefresh?: boolean) => {
    setModalOpen(false);
    if (shouldRefresh === true) {
      fetchShipments();
    }
  };

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<any>(null);

  // Open edit modal
  const handleEdit = (shipment: any) => {
    onClose();
    setSelectedShipment(shipment);
    setEditModalVisible(true);
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "view") {
      navigation.navigate("ChinaShipmentDetailsReport", { shipment: order });
    } else {
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    }
  };

  useEffect(() => {
    if (isFocused) {
      fetchShipments();
    }
  }, [isFocused, sort, status, pageSize, page, search]);

  useEffect(() => {
    setTableHeaders(
      isLargeDevice
        ? translateHeaders(shipmentHeaders, t, chinaShipmentTranslationMap)
        : isMediumDevice
          ? translateHeaders(mdShipmentHeaders, t, chinaShipmentTranslationMap)
          : translateHeaders(smShipmentHeaders, t, chinaShipmentTranslationMap),
    );

    setNestedTableHeaders(
      translateHeaders(mdNestedShipmentHeaders, t, chinaShipmentTranslationMap),
    );
  }, [isLargeDevice, isMediumDevice, language]);

  const handleSearch = (searchedText: string) => {
    // Reset to first page in Redux when searching
    dispatch(updatePage(1));
    if (searchedText) {
      dispatch(searchShipment(searchedText));
    } else {
      dispatch(searchShipment(""));
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      dispatch(updatePage(newPage));
    }
  };

  const handleSort = (key: any) => {
    let direction = "asc";
    if (sort.key === key && sort.direction === "asc") {
      direction = "desc";
    }
    dispatch(updateSort({ key, direction }));
  };

  const [uploadLoading, setUploadLoading] = useState(false);
  const toast = useToast();

  const selectAndUploadExcel = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
        ],
        copyToCacheDirectory: true,
      });

      if (result.canceled) return;

      const file = result.assets[0];
      setUploadLoading(true);

      const uniqueId = new Date().getTime();
      const uniqueFileName = uniqueId + "_" + file.name;

      const s3UploadResponse = await pushFileToS3(
        "POST",
        file.uri,
        uniqueFileName,
        file.mimeType || ".xlms",
        "china_shipments",
      );

      if (s3UploadResponse) {
        toast.show("Excel uploaded successfully", { type: "success" });
        fetchShipments(); // refresh after upload
      } else {
        toast.show("Upload failed", { type: "danger" });
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.show("Upload failed", { type: "danger" });
    } finally {
      setUploadLoading(false);
    }
  };

  const renderUtilityCards = () => {
    const alertActions: Array<{
      key: string;
      label: string;
      icon: any;
      color: string;
      textColor?: string;
      onPress?: () => void;
    }> = [
      {
        key: "generate",
        label: t("generateShipment"),
        icon: "plus",
        color: "#B8B8B8",
        onPress: () => navigateToOrder(shipments, "generate_shipment"),
      },
    ];

    alertActions.push({
      key: "incoming",
      label: t("incomingOrder"),
      icon: "bell",
      color: incomingBellStatus ? "#00a434" : "#B8B8B8",
      onPress: incomingBellStatus
        ? () => navigateToOrder([], "incoming_orders")
        : undefined,
    });

    alertActions.push({
      key: "recent_shipments",
      label: t("recentShipments"),
      icon: "history",
      color: "#56B7FD",
      onPress: () => navigateToOrder([], "recent_shipments"),
    });

    alertActions.push({
      key: "upload_shipments",
      label: uploadLoading ? "Uploading..." : "Upload Shipments",
      icon: uploadLoading ? "loading" : "upload",
      color: "#56B7FD",
      onPress: uploadLoading ? undefined : selectAndUploadExcel,
    });

    return (
      <View
        style={[
          styles.utilityContainer,
          shouldStackUtilityCards && styles.utilityContainerStacked,
        ]}
      >
        <View
          style={[
            styles.sectionCard,
            styles.denseCard,
            styles.alertsCard,
            shouldUseCompactControls && styles.sectionCardCompact,
          ]}
        >
          <View style={styles.alertActionsWrapper}>
            <View style={styles.alertActionsContainer}>
              {alertActions.map((action) => (
                <TouchableOpacity
                  key={action.key}
                  style={[
                    styles.alertActionItem,
                    !action.onPress && styles.alertActionDisabled,
                  ]}
                  onPress={action.onPress}
                  activeOpacity={action.onPress ? 0.85 : 1}
                >
                  <MaterialCommunityIcons
                    name={action.icon}
                    size={16}
                    color={action.color}
                  />
                  <Text
                    style={[
                      styles.alertActionText,
                      action.textColor && { color: action.textColor },
                    ]}
                  >
                    {action.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
        <View
          style={[
            styles.sectionCard,
            styles.denseCard,
            styles.searchCard,
            shouldUseCompactControls && styles.sectionCardCompact,
          ]}
        >
          <LabelBannerWithSearchBar
            titleName={`${t("totalItems")}: ${totalCount ?? shipments?.length ?? 0}`}
            handleSearch={handleSearch}
            setRefresh={() => {
              dispatch(updatePage(1));
              fetchShipments(selectedDateFilter || undefined);
            }}
            dataToExport={shipments}
            fileName="ChinaShipmentReport"
            sheetName="Shipment Mains"
          />
        </View>
      </View>
    );
  };

  const getDateRange = (filter: string) => {
    const now = new Date();
    let dateFrom = new Date();

    switch (filter) {
      case "Last 6 Months":
        dateFrom.setMonth(now.getMonth() - 6);
        break;
      case "Last Month":
        dateFrom.setMonth(now.getMonth() - 1);
        break;
      case "Last Week":
        dateFrom.setDate(now.getDate() - 7);
        break;
      case "Today":
        dateFrom.setDate(now.getDate());
        break;
      default:
        return { dateFrom: null, dateTo: null };
    }

    return {
      dateFrom: dateFrom.toISOString(),
      dateTo: now.toISOString(),
    };
  };

  const renderDateFilters = () => {
    return (
      <View style={styles.quickFilterBar}>
        <Text style={styles.quickFilterLabel}>{t("dateFilter")}</Text>

        <View style={styles.filterChipsWrap}>
          {dateFilterList.map((option, index) => {
            const isActive = selectedDateFilter === option.key;

            return (
              <TouchableOpacity
                key={index}
                style={[styles.filterChip, isActive && styles.filterChipActive]}
                onPress={() => {
                  const newFilter =
                    selectedDateFilter === option.key ? null : option.key;

                  setSelectedDateFilter(newFilter);

                  dispatch(updatePage(1));

                  fetchShipments(newFilter || undefined);
                }}
              >
                <Text
                  style={
                    isActive
                      ? styles.filterChipTextActive
                      : styles.filterChipText
                  }
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };
  return (
    <View style={styles.container}>
      <View style={styles.topContent}>
        {renderDateFilters()}
        {renderUtilityCards()}
      </View>

      <ScrollView
        style={styles.contentContainer}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loading ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: themeColors.surface, flex: 1 }}>
            <ChinaTrackerDataTableWithPagination3
              isToExpandAllRows={false}
              isRowsExpandable={true}
              headers={tableHeaders}
              isNestedDataLoaded={loading}
              rows={shipments}
              key="rowKeyValue"
              rowKeyValue="_id"
              columnsToNotHaveThousandSeparator={
                columnsToNotHaveThousandsSeparator
              }
              itemsPerPage={pageSize}
              handleSortColumn={handleSort}
              operationActions={navigateToOrder}
              itemTracker={true}
              nestedHeader={nestedTableHeaders}
              nestedItemKeyValue="items"
              useModal={false}
              setModalVisible={() => {}}
              handleRowClick={() => {}}
              setSelected={() => {}}
              handleDeleteRow={() => {}}
              t={t}
              page={page - 1}
              setPage={handlePageChange}
            />
          </View>
        )}
      </ScrollView>

      {modalOpen && selectedEnquiryType === "generate_shipment" && (
        <ChinaShipmentContainerSearchModal
          onClose={onClose}
          visible={modalOpen}
          setSelectedEnquiryType={setSelectedEnquiryType}
        />
      )}

      {modalOpen && selectedEnquiryType === "generate_shipment_form" && (
        <ChinaShipmentGenerateFormModal onClose={onClose} visible={modalOpen} />
      )}

      {modalOpen && selectedEnquiryType === "incoming_orders" && (
        <ChinaShipmentIncomingOrders
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
          handleEdit={handleEdit}
        />
      )}

      {/* Shipment Edit Before Approval */}
      <ChinaShipmentEditFormModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        shipment={selectedShipment}
      />

      {modalOpen && selectedEnquiryType === "recent_shipments" && (
        <ChinaRecentShipmentsModal
          onClose={() => onClose()}
          visible={modalOpen}
        />
      )}
    </View>
  );
}

function createStyles(colors: any) {
  return StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.surface,
  },
  headerContainer: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 8,
    backgroundColor: colors.surface,
  },
  topContent: {
    gap: 12,
    marginBottom: 10,
  },
  sectionCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 12,
    shadowColor: "rgba(15, 23, 42, 0.08)",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 2,
  },
  denseCard: {
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  sectionCardCompact: {
    padding: 8,
    borderRadius: 10,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: colors.text,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: colors.text,
    marginBottom: 6,
  },
  sectionTitleCompact: {
    fontSize: 12,
    marginBottom: 4,
  },
  filterChipRow: {
    paddingRight: 8,
    paddingBottom: 2,
  },
  filterChipRowCompact: {
    paddingBottom: 0,
  },
  filterCard: {
    flexDirection: "row",
    gap: 12,
  },
  filterCardStacked: {
    flexDirection: "column",
  },
  cardHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  headerLabelGroup: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  filterColumnLabel: {
    flex: 1,
  },
  filterColumn: {
    flex: 1,
  },
  utilityContainer: {
    flexDirection: "row",
    gap: 16,
  },
  utilityContainerStacked: {
    flexDirection: "column",
  },
  alertsCard: {
    flex: 1,
  },
  alertActionsWrapper: {
    minHeight: 64,
    justifyContent: "center",
  },
  alertActionsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-start",
    alignItems: "center",
  },
  alertActionItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 999,
    backgroundColor: colors.surfaceSubtle,
  },
  alertActionDisabled: {
    opacity: 0.5,
  },
  alertActionText: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.text,
  },
  searchCard: {
    flex: 1,
    justifyContent: "center",
  },
  contentContainer: {
    flex: 1,
  },
  spinnerContainer: {
    position: "absolute",
    top: 100,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
  },

  quickFilterBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.surface,
    paddingVertical: 16,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  quickFilterChipsContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 12,
  },
  quickFilterLabel: {
    fontWeight: "700",
    fontSize: 13,
    color: colors.textSecondary,
    marginRight: 10,
  },
  filterChip: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: colors.surfaceSubtle,
    borderRadius: 999,
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  filterChipActive: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterChipText: {
    fontSize: 13,
    color: colors.textSecondary,
    fontWeight: "500",
  },
  filterChipTextActive: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },

  filterChipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 8,
  },
});
}
